/**
 * Reset Password Page
 *
 * Allows users to set a new password using reset token from email link.
 * Validates password strength and matching passwords.
 * Handles expired/invalid tokens and redirects to login on success.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  PasswordInput,
  Button,
  Alert,
  Stack,
  Center,
  List,
  ThemeIcon,
  Anchor,
  Loader,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';
import { supabase } from '@/lib/supabase';
import { validatePasswordStrength } from '@/utils/validation';

interface ResetPasswordForm {
  password: string;
  confirm_password: string;
}

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSessionReady, setIsSessionReady] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  // Handle the Supabase password recovery flow
  // When user clicks the reset link in email, Supabase redirects here with tokens in URL hash
  useEffect(() => {
    // Check if there's already a session (user came from password reset email)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setIsSessionReady(true);
      }
      setIsCheckingSession(false);
    });

    // Listen for PASSWORD_RECOVERY event from Supabase
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsSessionReady(true);
        setIsCheckingSession(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const form = useForm<ResetPasswordForm>({
    initialValues: {
      password: '',
      confirm_password: '',
    },
    validate: {
      password: validatePasswordStrength,
      confirm_password: (value: string, values: ResetPasswordForm) =>
        value === values.password ? null : 'Passwords do not match',
    },
  });

  const handleSubmit = async (values: ResetPasswordForm) => {
    setError(null);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: values.password,
      });

      if (error) {
        setError(error.message);
      } else {
        // Password reset successful, sign out and redirect to login
        await supabase.auth.signOut();
        navigate('/login', {
          state: { message: 'Password updated successfully. Please log in.' },
          replace: true,
        });
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Password strength indicators
  const getPasswordStrength = (password: string) => {
    const requirements = [
      { met: password.length >= 8, label: 'At least 8 characters' },
      { met: /[A-Z]/.test(password), label: 'One uppercase letter' },
      { met: /[a-z]/.test(password), label: 'One lowercase letter' },
      { met: /[0-9]/.test(password), label: 'One number' },
    ];
    return requirements;
  };

  const passwordStrength = getPasswordStrength(form.values.password);

  // Show loading while checking session
  if (isCheckingSession) {
    return (
      <Container maw={{ base: '100%', xs: 420 }} px={{ base: 'md', xs: 0 }} my={40}>
        <Center>
          <Stack gap="md" w="100%" align="center">
            <Title ta="center">Reset Password</Title>
            <Loader size="lg" />
            <Text c="dimmed" size="sm">
              Verifying reset link...
            </Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // If session not ready (invalid/expired link), show error
  if (!isSessionReady) {
    return (
      <Container maw={{ base: '100%', xs: 420 }} px={{ base: 'md', xs: 0 }} my={40}>
        <Center>
          <Stack gap="md" w="100%">
            <Title ta="center">Reset Password</Title>

            <Paper withBorder p={30} mt={30} radius="md">
              <Stack gap="md">
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  title="Error"
                  color="red"
                >
                  Invalid or expired reset link. Please request a new password reset.
                </Alert>

                <Button component={Link} to="/forgot-password" variant="outline" fullWidth>
                  Request New Reset Link
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Center>
      </Container>
    );
  }

  return (
    <Container maw={{ base: '100%', xs: 420 }} px={{ base: 'md', xs: 0 }} my={40}>
      <Center>
        <Stack gap="md" w="100%">
          <Title ta="center">Reset Password</Title>
          <Text c="dimmed" size="sm" ta="center">
            Enter your new password
          </Text>

          <Paper withBorder p={30} mt={30} radius="md">
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="md">
                {error && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Error"
                    color="red"
                  >
                    {error}
                  </Alert>
                )}

                <PasswordInput
                  label="New Password"
                  placeholder="Create a strong password"
                  required
                  maxLength={128}
                  {...form.getInputProps('password')}
                />

                {form.values.password.length > 0 && (
                  <List
                    spacing="xs"
                    size="sm"
                    center
                    styles={{ item: { fontSize: '0.875rem' } }}
                  >
                    {passwordStrength.map((requirement, index) => (
                      <List.Item
                        key={index}
                        icon={
                          <ThemeIcon
                            color={requirement.met ? 'teal' : 'gray'}
                            size={20}
                            radius="xl"
                          >
                            {requirement.met ? (
                              <IconCheck size={12} />
                            ) : (
                              <IconX size={12} />
                            )}
                          </ThemeIcon>
                        }
                      >
                        {requirement.label}
                      </List.Item>
                    ))}
                  </List>
                )}

                <PasswordInput
                  label="Confirm Password"
                  placeholder="Re-enter your password"
                  required
                  maxLength={128}
                  {...form.getInputProps('confirm_password')}
                />

                <Button type="submit" fullWidth loading={isLoading}>
                  Reset Password
                </Button>

                <Text size="sm" ta="center" c="dimmed">
                  Remember your password?{' '}
                  <Anchor component={Link} to="/login" size="sm">
                    Sign in
                  </Anchor>
                </Text>
              </Stack>
            </form>
          </Paper>
        </Stack>
      </Center>
    </Container>
  );
};
